import { useQueryClient } from "@tanstack/react-query";
import {
  ColumnFiltersState,
  PaginationState,
  SortingFn,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Row,
  Table as BsTable,
} from "react-bootstrap";
import type { UnpackResponse } from "../../../api/src/lib";
import type { ThirteenfController } from "../../../api/src/reports/thirteenf.controller";
import Loading from "../Loading";
import HelpTooltip from "../components/HelpTooltip";
import MultiSelector from "../components/MultiSelector";
import { withOverrideMarker } from "../components/OverrideMarker";
import SubmitButton from "../components/SubmitButton";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, useTable } from "../components/Table/Table";
import { multiSelectIncludes } from "../components/Table/filters";
import {
  onColumnFiltersChange,
  onPaginationChange,
  onSortingChange,
  useTableSettings,
} from "../components/Table/tableSettings";
import type { SecurityMasterRow } from "../firm/SecurityMaster";
import SecurityMasterModal from "../firm/SecurityMasterModal";
import {
  refusalSentence,
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../lib/api";
import { useRolesResolution } from "../lib/auth";
import { toISODateNoTimezone } from "../lib/date";
import { processFileResponseOrThrow } from "../lib/file";
import { formatCurrency } from "../lib/numbers";
import ReportsNav from "./ReportsNav";

type FilingInner = UnpackResponse<ThirteenfController["getFiling"]>["data"];
type Decision = FilingInner["decisions"][number];
type ReviewerDecision = "report" | "de_minimis" | "not_13f" | "unset";

const BUCKET_LABELS: Record<string, string> = {
  report: "13-F to Report",
  de_minimis: "De Minimis (Omitted)",
  excluded_deleted: "Excluded (DELETED)",
  missing_cusip: "Missing or Unrecognized CUSIP",
  audit_possible_missed: "Audit — Possible Missed 13F",
  not_13f: "Not 13-F",
};

const BUCKET_VARIANT: Record<string, string> = {
  report: "success",
  de_minimis: "info",
  excluded_deleted: "secondary",
  missing_cusip: "warning",
  audit_possible_missed: "danger",
  not_13f: "light",
};

/**
 * responseCallback for the download queries: hand the file to the browser.
 *
 * The throwing variant is required so that a blob, object-URL or anchor failure
 * after a 200 reaches the caller instead of resolving as a success (APP-1453).
 *
 * The returned `true` is only what the `responseCallback` signature needs —
 * `processFileResponseOrThrow` resolves with undefined, and React Query rejects
 * an undefined query result. It is not the value React Query stores:
 * useAuthenticatedQuery feeds this result into `queryFn`, and the download
 * queries use `queryFn: async () => true`, which ignores its argument.
 */
async function downloadFile(res: Response): Promise<boolean> {
  await processFileResponseOrThrow(res);
  return true;
}

/**
 * The text a failed request shows the reviewer (APP-2168): the sentence the api
 * refused a 4xx with, else "An error occurred" — a 5xx, a 4xx with no sentence,
 * a network failure or any other error.
 */
function errorMessage(e: unknown) {
  return refusalSentence(e) ?? "An error occurred";
}

// Bootstrap's badge text colour defaults to white, and `.bg-*` sets only the
// background, so every bucket light enough to wash out white text needs its
// text colour set explicitly. Against white, `light` is 1.00:1, `info` 1.96:1
// and `warning` 1.63:1, all short of WCAG AA (4.5:1); `dark` (#212529) lifts
// them to 15.43:1, 7.88:1 and 9.46:1. The `bg-*` values are unchanged, and
// `danger`, `success` and `secondary` already clear AA against white.
const BUCKET_TEXT_VARIANT: Record<string, string> = {
  de_minimis: "dark",
  missing_cusip: "dark",
  not_13f: "dark",
};

/**
 * The Bucket and Class multi-selects offer only the values the loaded filing
 * actually carries, so a reviewer is never given an option that matches nothing
 * (SecurityMaster builds its Category options the same way). Class is nullable
 * in the database, so the blank value gets a label of its own rather than an
 * unlabelled entry the reviewer cannot interpret.
 */
const NO_CLASS_LABEL = "No Class";

/**
 * Sorts the 13-F column's three states — Unknown, No, Yes.
 *
 * APP-1444: the accessor yields `boolean | null`, and with no `sortingFn` the
 * table falls through to `sortingFns.basic`, i.e.
 * `compareBasic = (a, b) => (a === b ? 0 : a > b ? 1 : -1)`. That is not a
 * total order over `{true, false, null}`: `false > null` and `null > false`
 * are both false (each coerces to 0), so `compareBasic` returns -1 for both
 * `(false, null)` and `(null, false)` — each value claims to precede the
 * other. Only `true` orders consistently. The result is that the Unknown rows
 * (the `missing_cusip` / `audit_possible_missed` holdings this column exists
 * to surface) interleave with the No rows in whatever permutation the engine's
 * sort happens to produce, so clicking the header does not group them.
 *
 * Ranking the states explicitly makes the comparator a total order and the
 * grouping deterministic: Unknown, then No, then Yes.
 */
const is13fRank = (value: boolean | null | undefined) =>
  value === null || value === undefined ? -1 : value ? 1 : 0;

const is13fSortingFn: SortingFn<Decision> = (rowA, rowB, columnId) =>
  is13fRank(rowA.getValue<boolean | null>(columnId)) -
  is13fRank(rowB.getValue<boolean | null>(columnId));

/** localStorage key for the Holdings table's sorting / filters / page size. */
const HOLDINGS_TABLE_NAME = "13f-holdings";

const filingQueryKey = (filingId: number | null) =>
  ["reports", "13f", "filings", String(filingId)] as const;

/**
 * The workbook and the EDGAR XML download the same way and differ only in the
 * last path segment, which is also the last key segment — useAuthenticatedQuery
 * builds the URL from the query key. Both routes return a StreamableFile, so
 * one response type covers them.
 *
 * retry: false keeps a failing download to one request instead of four spread
 * over seven seconds of backoff — these fire on a button press, so the reviewer
 * waits for them. enabled: false leaves them to `refetch()` from the finalize
 * handler; nothing downloads on render.
 */
function useFileDownload(filingId: number, file: "workbook" | "xml") {
  return useAuthenticatedQuery<
    UnpackResponse<ThirteenfController["downloadWorkbook"]>,
    boolean
  >({
    queryKey: [...filingQueryKey(filingId), file],
    queryFn: async () => true,
    queryOptions: { enabled: false, retry: false },
    responseCallback: downloadFile,
  });
}

const Filing13f = () => {
  const queryClient = useQueryClient();
  const [date, setDate] = useState<Date>(new Date());
  const [filingId, setFilingId] = useState<number | null>(null);
  // The date (YYYY-MM-DD) of the last start that succeeded in this visit: the
  // button reads "Recompute" only while "As-of date" holds it (APP-2172).
  const [startedDate, setStartedDate] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  // Whether the button read "Recompute" when the failed start was selected.
  const [failedRecompute, setFailedRecompute] = useState(false);

  const startMutation = useAuthenticatedMutationNew<
    UnpackResponse<ThirteenfController["startFiling"]>,
    { filingId: number },
    { date: string }
  >({
    mutationKey: ["POST", "reports", "13f", "filings"],
    mutationFn: ({ data }) => ({ filingId: data.filingId }),
  });

  const {
    data: filing,
    isError: isFilingError,
    isFetching: isFilingFetching,
    refetch: refetchFiling,
  } = useAuthenticatedQuery<
    UnpackResponse<ThirteenfController["getFiling"]>,
    FilingInner
  >({
    queryKey: filingQueryKey(filingId),
    queryFn: async ({ data }) => data,
    queryOptions: { enabled: filingId !== null },
  });

  const isoDate = toISODateNoTimezone(date);
  const isRecompute = startedDate !== null && isoDate === startedDate;

  const onStart = useCallback(async () => {
    setStartError(null);

    // A rejected mutateAsync is otherwise an unhandled rejection: SubmitButton
    // does not catch, and the QueryClient sets no global mutation onError, so
    // a 403 or 500 left the reviewer pressing a button that did nothing
    // (APP-1453).
    let result: { filingId: number };
    try {
      result = await startMutation.mutateAsync({ date: isoDate });
    } catch (e) {
      setFailedRecompute(isRecompute);
      setStartError(errorMessage(e));
      return;
    }

    setFilingId(result.filingId);
    setStartedDate(isoDate);
    // A recompute of an existing filing upserts on (firm, as-of date) and
    // returns the same id, so the query key is unchanged and the cached
    // pre-recompute rows would be served. Invalidate the key to read them back.
    //
    // exact: true because the two download keys extend this one. A prefix
    // invalidate reaches them too, and today it is inert only because they are
    // `enabled: false` and invalidateQueries refetches active queries alone —
    // enabling either would turn every recompute into a second download.
    await queryClient.invalidateQueries({
      queryKey: filingQueryKey(result.filingId),
      exact: true,
    });
  }, [startMutation, isoDate, isRecompute, queryClient]);

  return (
    <TabContainerWithTabs tabs={ReportsNav}>
      <h4 className="mb-3">13-F Filing</h4>
      <p className="text-muted">
        Classify firm holdings against the SEC 13F securities list for an as-of
        date, review each position, then generate the filing workbook and EDGAR
        XML. The SEC list used is the most recent one on or before the date.
      </p>

      <Row className="align-items-end mb-3">
        <Col md={4}>
          <Form.Group controlId="filing-13f-date">
            <Form.Label>As-of date</Form.Label>
            <Form.Control
              type="date"
              value={isoDate}
              onChange={(e) =>
                e.target.value &&
                setDate(new Date(`${e.target.value}T00:00:00`))
              }
            />
          </Form.Group>
        </Col>
        <Col md="auto" className="d-flex align-items-center gap-1">
          <SubmitButton
            label={isRecompute ? "Recompute" : "Start review"}
            isSubmitting={startMutation.isPending}
            onClick={onStart}
          />
          <HelpTooltip tooltip={<RecomputeHelp />} />
        </Col>
      </Row>

      {startError && (
        <Alert variant="danger">
          <Alert.Heading as="h5">
            {failedRecompute
              ? "Could not recompute the review"
              : "Could not start the review"}
          </Alert.Heading>
          <p className="mb-0">{startError}</p>
        </Alert>
      )}

      {/* A failed read, also a read again after a decision or an edit, shows
          the error in place of the review; a stale filing is not drawn under it.
          While "Try again" reads again, neither shows. */}
      {filingId !== null && isFilingError
        ? !isFilingFetching && (
            <Alert variant="danger">
              An error occurred{" "}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => refetchFiling()}
              >
                Try again
              </Button>
            </Alert>
          )
        : filing &&
          filingId !== null && (
            <ReviewPanel
              filing={filing}
              filingId={filingId}
              onDecisionSaved={() => refetchFiling()}
            />
          )}
    </TabContainerWithTabs>
  );
};

function ReviewPanel({
  filing,
  filingId,
  onDecisionSaved,
}: {
  filing: FilingInner;
  filingId: number;
  onDecisionSaved: () => void;
}) {
  const [finalizeMessage, setFinalizeMessage] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [downloadErrors, setDownloadErrors] = useState<string[]>([]);
  const [editingRow, setEditingRow] = useState<SecurityMasterRow | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const finalizeMutation = useAuthenticatedMutationNew<
    UnpackResponse<ThirteenfController["finalize"]>,
    { errors: string[] },
    { withXml: boolean }
  >({
    mutationKey: [
      "POST",
      "reports",
      "13f",
      "filings",
      String(filingId),
      "finalize",
    ],
    mutationFn: ({ data }) => data,
  });

  const downloadWorkbook = useFileDownload(filingId, "workbook");
  const downloadXml = useFileDownload(filingId, "xml");

  const onFinalize = useCallback(
    async (withXml: boolean) => {
      // Clear every alert before the attempt runs, not after it succeeds. The
      // validation-error path used to return before the reset, leaving the
      // previous attempt's "Download failed" alert on screen describing a
      // download this pass never made (APP-1453).
      setErrors([]);
      setDownloadErrors([]);
      setFinalizeError(null);
      setFinalizeMessage(null);

      // An unhandled rejection here left the reviewer with no sign the POST
      // had failed at all.
      let result: { errors: string[] };
      try {
        result = await finalizeMutation.mutateAsync({ withXml });
      } catch (e) {
        setFinalizeError(errorMessage(e));
        return;
      }

      if (result.errors.length > 0) {
        setErrors(result.errors);
        return;
      }

      // The POST already finalized the filing, so attempt both files and
      // report whichever did not arrive rather than stopping at the first
      // failure — the reviewer should still get the file that does work.
      const failures: string[] = [];
      for (const file of [
        { label: "workbook", refetch: downloadWorkbook.refetch },
        ...(withXml
          ? [{ label: "EDGAR XML", refetch: downloadXml.refetch }]
          : []),
      ]) {
        try {
          // throwOnError is required: a plain refetch() resolves even when the
          // request failed, which is how a 500 on the workbook route reached
          // the success message below unnoticed (APP-1447).
          await file.refetch({ throwOnError: true });
        } catch (e) {
          failures.push(`${file.label}: ${errorMessage(e)}`);
        }
      }

      if (failures.length > 0) {
        setDownloadErrors(failures);
        return;
      }
      setFinalizeMessage(
        withXml
          ? "Filing finalized. Workbook and EDGAR XML downloaded."
          : "Filing finalized. Workbook downloaded.",
      );
    },
    [finalizeMutation, downloadWorkbook, downloadXml],
  );

  const decisions = filing.decisions;

  // Only show the override legend when at least one holding's 13-F status is
  // firm-overridden, as the Security Master does for its own marked columns.
  const hasIs13fOverrides = useMemo(
    () => decisions.some((d) => d.is_is13f_override),
    [decisions],
  );

  const columnHelper = useMemo(() => createColumnHelper<Decision>(), []);

  const bucketOptions = useMemo(
    () =>
      Array.from(new Set(decisions.map((d) => d.bucket)))
        .map((bucket) => ({
          label: BUCKET_LABELS[bucket] ?? bucket,
          value: bucket as string,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [decisions],
  );

  const classOptions = useMemo(
    () =>
      Array.from(new Set(decisions.map((d) => d.title_of_class ?? "")))
        .sort()
        .map((titleOfClass) => ({
          label: titleOfClass === "" ? NO_CLASS_LABEL : titleOfClass,
          value: titleOfClass,
        })),
    [decisions],
  );

  // Typed `any` on the header for the same reason SecurityMaster's
  // SecurityCategoryFilter is: ColumnMeta.filterComponent is declared against
  // one column's TValue, and a component written for a concrete Header type
  // will not satisfy it.
  const BucketFilter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ header }: { header: any }) => {
      const onChange = (values: string[]) => {
        header.column.setFilterValue(values);
      };

      return (
        <MultiSelector
          options={bucketOptions}
          placeholder="All Buckets"
          setValue={onChange}
          value={header.column.getFilterValue() ?? []}
        />
      );
    },
    [bucketOptions],
  );

  const ClassFilter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ header }: { header: any }) => {
      const onChange = (values: string[]) => {
        header.column.setFilterValue(values);
      };

      return (
        <MultiSelector
          options={classOptions}
          placeholder="All Classes"
          setValue={onChange}
          value={header.column.getFilterValue() ?? []}
        />
      );
    },
    [classOptions],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((d) => d.issuer_name ?? "", {
        id: "issuer",
        header: () => "Issuer",
        cell: (info) => info.getValue() || "-",
        enableSorting: true,
      }),
      columnHelper.accessor((d) => d.title_of_class ?? "", {
        id: "titleOfClass",
        header: () => "Class",
        cell: (info) => info.getValue() || "-",
        filterFn: multiSelectIncludes,
        enableSorting: true,
        meta: { filterComponent: ClassFilter },
      }),
      columnHelper.accessor((d) => d.cusip ?? "", {
        id: "cusip",
        header: () => "CUSIP",
        cell: (info) => info.getValue() || "-",
        enableSorting: true,
      }),
      // Value and Shares arrive from the API as strings (PersistedDecision's
      // `market_value` and `shares` are `string | null`), and the cells render
      // them formatted. Both accessors therefore return a number: the column's
      // sort compares the accessor value, so a string here would order $9
      // after $100. Filtering is off — a substring match over a formatted
      // amount is not a useful way to narrow this table.
      columnHelper.accessor((d) => Number(d.market_value ?? 0), {
        id: "marketValue",
        header: () => "Value",
        cell: (info) => formatCurrency(info.getValue()),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { className: "text-end", headerClassName: "text-end" },
      }),
      columnHelper.accessor((d) => Number(d.shares ?? 0), {
        id: "shares",
        header: () => "Shares",
        cell: (info) => info.getValue().toLocaleString(),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { className: "text-end", headerClassName: "text-end" },
      }),
      // APP-1444: the security's effective 13-F flag for this firm
      // (COALESCE(override, base)), null when the symbol resolves to no
      // security_base row — the audit case a malformed CUSIP produces. The "*"
      // marks a value the firm set by override, and is display-only: it is not
      // in the accessor, so it never reaches the sort. Filtering is off for the
      // same reason — with the marker out of the value a filter here could only
      // split Yes from No, which the Bucket column already does.
      columnHelper.accessor((d) => d.is_13f, {
        id: "is13f",
        header: () => "13-F",
        cell: (info) => {
          const value = info.getValue();
          const display =
            value === null || value === undefined
              ? "Unknown"
              : value
                ? "Yes"
                : "No";
          return withOverrideMarker(
            display,
            info.row.original.is_is13f_override,
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: is13fSortingFn,
      }),
      columnHelper.accessor((d) => d.bucket as string, {
        id: "bucket",
        header: () => "Bucket",
        cell: (info) => (
          <Badge
            bg={BUCKET_VARIANT[info.getValue()] ?? "secondary"}
            text={BUCKET_TEXT_VARIANT[info.getValue()]}
          >
            {BUCKET_LABELS[info.getValue()] ?? info.getValue()}
          </Badge>
        ),
        filterFn: multiSelectIncludes,
        enableSorting: true,
        meta: { filterComponent: BucketFilter },
      }),
      columnHelper.accessor((d) => d.reviewer_decision as ReviewerDecision, {
        id: "decision",
        header: () => "Decision",
        cell: (info) => (
          <DecisionButtons
            current={info.getValue()}
            decisionId={info.row.original.thirteenf_filing_decision_id}
            filingId={filingId}
            onSaved={onDecisionSaved}
          />
        ),
        enableSorting: true,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        header: () => "",
        cell: (info) => {
          const d = info.row.original;
          return (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => {
                setEditingRow({
                  symbol: d.symbol,
                  description: d.issuer_name ?? undefined,
                  cusip: d.cusip ?? undefined,
                  is13f:
                    d.reviewer_decision === "unset"
                      ? null
                      : d.reviewer_decision !== "not_13f",
                  // Unlike is13f above — which carries the reviewer's call for
                  // this filing — this is about the security, so the modal can
                  // mark the field as firm-overridden (APP-1444).
                  isIs13fOverride: d.is_is13f_override,
                });
                setShowEditModal(true);
              }}
            >
              Edit
            </Button>
          );
        },
      }),
    ],
    [columnHelper, BucketFilter, ClassFilter, filingId, onDecisionSaved],
  );

  const [tableSettings, setTableSettings] = useTableSettings(
    HOLDINGS_TABLE_NAME,
    { sorting: [{ id: "issuer", desc: false }], filters: [] },
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableSettings.filters,
  );
  const [sorting, setSorting] = useState<SortingState>(tableSettings.sorting);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: tableSettings.pageSize,
    pageIndex: 0,
  });

  const persistColumnFilters = onColumnFiltersChange(
    columnFilters,
    setColumnFilters,
    tableSettings,
    setTableSettings,
  );

  const persistSorting = onSortingChange(
    sorting,
    setSorting,
    tableSettings,
    setTableSettings,
  );

  // autoResetAll: false because saving a decision refetches the filing, which
  // hands the table a new rows array. Left on, that would throw away the
  // reviewer's page, sort and filters on every button press.
  //
  // But autoResetAll is not scoped to data changes: table-core fires
  // _autoResetPageIndex from getFilteredRowModel too, so switching it off also
  // kills the page reset that filtering relies on. getPaginationRowModel slices
  // without clamping, so a reviewer who filters from page 7 down to two matches
  // would get rows.slice(60, 70) of a 2-row model — a blank table for a search
  // that did match, which reads as "not in the filing". Reset the page from the
  // filter handler instead, where a refetch cannot trigger it.
  //
  // getSortedRowModel fires the same reset, so sorting needs the same handler.
  // It cannot empty the table — re-sorting does not change the row count — but
  // a reviewer who sorts by Value to find the largest holdings is asking for
  // the top of the new order, and without this lands on rows 61-70 of it.
  const { table: holdingsTable } = useTable({
    columns,
    data: decisions,
    getRowId: (d: Decision) => String(d.thirteenf_filing_decision_id),
    state: { columnFilters, sorting, pagination },
    onColumnFiltersChange: (updater) => {
      persistColumnFilters(updater);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    onSortingChange: (updater) => {
      persistSorting(updater);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    onPaginationChange: onPaginationChange(
      pagination,
      setPagination,
      tableSettings,
      setTableSettings,
    ),
    autoResetAll: false,
  });

  return (
    <>
      <div className="d-flex gap-3 mb-3">
        <div className="d-flex align-items-center gap-1">
          <SubmitButton
            label="Finalize (workbook only)"
            isSubmitting={finalizeMutation.isPending}
            onClick={() => onFinalize(false)}
          />
          <HelpTooltip tooltip={<FinalizeWorkbookHelp />} />
        </div>
        <div className="d-flex align-items-center gap-1">
          <SubmitButton
            label="Finalize with EDGAR XML"
            variant="secondary"
            isSubmitting={finalizeMutation.isPending}
            onClick={() => onFinalize(true)}
          />
          <HelpTooltip tooltip={<FinalizeXmlHelp />} />
        </div>
      </div>

      {finalizeError && (
        <Alert variant="danger">
          <Alert.Heading>Could not finalize the filing</Alert.Heading>
          <p className="mb-0">{finalizeError}</p>
        </Alert>
      )}
      {errors.length > 0 && (
        <Alert variant="danger">
          <Alert.Heading>Cannot generate XML yet</Alert.Heading>
          <ul className="mb-0">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}
      {downloadErrors.length > 0 && (
        <Alert variant="danger">
          <Alert.Heading>Download failed</Alert.Heading>
          <p>
            The filing is finalized, but these files could not be downloaded.
            Press the same button again to retry.
          </p>
          <ul className="mb-0">
            {downloadErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}
      {finalizeMessage && <Alert variant="success">{finalizeMessage}</Alert>}

      <h5>Summary</h5>
      <BsTable size="sm" bordered className="mb-4" style={{ maxWidth: 540 }}>
        <thead>
          <tr>
            <th>Bucket</th>
            <th className="text-end">Count</th>
            <th className="text-end">Total Value</th>
          </tr>
        </thead>
        <tbody>
          {filing.summary.map((line) => (
            <tr key={line.bucket}>
              <td>
                <Badge
                  bg={BUCKET_VARIANT[line.bucket] ?? "secondary"}
                  text={BUCKET_TEXT_VARIANT[line.bucket]}
                >
                  {BUCKET_LABELS[line.bucket] ?? line.bucket}
                </Badge>
              </td>
              <td className="text-end">{line.count.toLocaleString()}</td>
              <td className="text-end">{formatCurrency(line.value)}</td>
            </tr>
          ))}
        </tbody>
      </BsTable>

      <h5>Holdings</h5>
      <Table table={holdingsTable} className="table-sm table-hover" />

      {hasIs13fOverrides && (
        <p className="text-muted small mt-2 mb-0">
          <span aria-hidden="true">*</span> indicates a value set by a
          firm-level override. Unmarked values come from the system default.
        </p>
      )}

      <SecurityMasterModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        editingRow={editingRow}
        existingSymbols={filing.decisions.map((d) => d.symbol)}
        onSave={() => {
          setShowEditModal(false);
          onDecisionSaved();
        }}
        sendOverridesOnlyWhenChanged
      />
    </>
  );
}

function DecisionButtons({
  current,
  decisionId,
  filingId,
  onSaved,
}: {
  current: ReviewerDecision;
  decisionId: number;
  filingId: number;
  onSaved: () => void;
}) {
  const mutation = useAuthenticatedMutationNew<
    UnpackResponse<ThirteenfController["setDecision"]>,
    unknown,
    { decision: ReviewerDecision }
  >({
    mutationKey: [
      "PATCH",
      "reports",
      "13f",
      "filings",
      String(filingId),
      "decisions",
      String(decisionId),
    ],
    mutationFn: ({ data }) => data,
  });

  const [saveError, setSaveError] = useState<string | null>(null);

  const choose = async (decision: ReviewerDecision) => {
    setSaveError(null);

    // Nothing catches a rejected PATCH otherwise, so a failed save looked
    // exactly like a successful one until the row was reloaded (APP-1453).
    try {
      await mutation.mutateAsync({ decision });
    } catch (e) {
      setSaveError(errorMessage(e));
      return;
    }

    onSaved();
  };

  return (
    <div>
      {current === "de_minimis" && (
        <div className="small text-muted mb-1">13-F (De Minimis)</div>
      )}
      <div className="d-flex gap-1">
        <Button
          size="sm"
          aria-pressed={current === "report"}
          variant={current === "report" ? "success" : "outline-success"}
          disabled={mutation.isPending}
          onClick={() => choose("report")}
        >
          13-F
        </Button>
        <Button
          size="sm"
          aria-pressed={current === "not_13f"}
          variant={current === "not_13f" ? "secondary" : "outline-secondary"}
          disabled={mutation.isPending}
          onClick={() => choose("not_13f")}
        >
          Not 13-F
        </Button>
      </div>
      {saveError && (
        <div className="text-danger small mt-1">Not saved: {saveError}</div>
      )}
    </div>
  );
}

function RecomputeHelp() {
  return (
    <>
      <p>
        <strong>Start review</strong> classifies the firm&apos;s holdings
        against the SEC 13F securities list for the as-of date and puts each
        holding in a bucket.
      </p>
      <p className="mb-0">
        <strong>Recompute</strong> re-runs that classification and rebuilds
        every bucket. Reviewer decisions already made are preserved: a holding
        you marked 13-F or Not 13-F keeps that decision.
      </p>
    </>
  );
}

function FinalizeWorkbookHelp() {
  return (
    <p className="mb-0">
      <strong>Finalize (workbook only)</strong> marks the filing finalized and
      downloads the review workbook, with a tab for each decision: 13-F to
      Report, 13-F (De Minimis), Not 13-F, and Unresolved &amp; Audit. It
      produces no EDGAR XML and does not validate the holdings.
    </p>
  );
}

function FinalizeXmlHelp() {
  return (
    <p className="mb-0">
      <strong>Finalize with EDGAR XML</strong> validates the holdings to be
      reported first. While there are validation errors it lists them and does
      not finalize the filing. With no errors it finalizes, then downloads the
      workbook and the EDGAR informationTable XML.
    </p>
  );
}

/**
 * Administrator gate for the page. Every `reports/13f` route is gated on the
 * Admin role, so a member reaching this URL directly would otherwise get a page
 * that starts a review and then fails each action with a bare 403.
 *
 * The wrapper exists because the gate has to run before the page's hooks: it
 * cannot be an early return inside Filing13f without making those hook calls
 * conditional.
 */
function Filing13fGate() {
  const { status, roles } = useRolesResolution();

  // An explicit status, rather than reading an empty role list as "still
  // loading": that sentinel had no terminal state, so a failed or hung token
  // refresh left this page spinning forever with no error and no exit
  // (APP-1453).
  if (status === "loading") {
    return (
      <TabContainerWithTabs tabs={ReportsNav}>
        <Loading />
      </TabContainerWithTabs>
    );
  }

  // Report the failure rather than treat it as "not an administrator": the
  // roles are unknown here, and demoting an administrator on a token flake
  // gives them a refusal they cannot act on.
  if (status === "error") {
    return (
      <TabContainerWithTabs tabs={ReportsNav}>
        <h4 className="mb-3">13-F Filing</h4>
        <Alert variant="danger">
          <Alert.Heading as="h5">Could not confirm your access</Alert.Heading>
          <p className="mb-0">
            Your permissions could not be read, so this page cannot tell whether
            you may prepare a 13-F filing. Reload the page to try again; if it
            keeps failing, sign out and back in.
          </p>
        </Alert>
      </TabContainerWithTabs>
    );
  }

  if (!roles.includes("Admin")) {
    return (
      <TabContainerWithTabs tabs={ReportsNav}>
        <h4 className="mb-3">13-F Filing</h4>
        <Alert variant="warning">
          <Alert.Heading as="h5">Admin required</Alert.Heading>
          <p className="mb-0">
            Preparing a 13-F filing changes how the firm classifies its
            securities, so it is restricted to administrators. Ask an
            administrator at your firm to run the filing.
          </p>
        </Alert>
      </TabContainerWithTabs>
    );
  }

  return <Filing13f />;
}

export default Filing13fGate;
